'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@kit/ui/button';
import { Calendar } from '@kit/ui/calendar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { Label } from '@kit/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Switch } from '@kit/ui/switch';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { generateOffersFromAuditionAction } from '../../server/actions/offer-generation.actions';

// ==================== Types ====================

type OfferType =
  | 'contract'
  | 'internship'
  | 'artistic_collaboration'
  | 'audition_invite'
  | 'workshop'
  | 'full_year'
  | 'short_term'
  | 'summer_program'
  | 'winter_program'
  | 'internship_program'
  | 'international_scholar'
  | 'other';

const OFFER_TYPES: { value: OfferType; labelKey: string }[] = [
  {
    value: 'contract',
    labelKey: 'auditions:generateOffers.offerTypes.contract',
  },
  {
    value: 'internship',
    labelKey: 'auditions:generateOffers.offerTypes.internship',
  },
  {
    value: 'artistic_collaboration',
    labelKey: 'auditions:generateOffers.offerTypes.artisticCollaboration',
  },
  {
    value: 'audition_invite',
    labelKey: 'auditions:generateOffers.offerTypes.auditionInvite',
  },
  {
    value: 'workshop',
    labelKey: 'auditions:generateOffers.offerTypes.workshop',
  },
  {
    value: 'full_year',
    labelKey: 'auditions:generateOffers.offerTypes.fullYear',
  },
  {
    value: 'short_term',
    labelKey: 'auditions:generateOffers.offerTypes.shortTerm',
  },
  {
    value: 'summer_program',
    labelKey: 'auditions:generateOffers.offerTypes.summerProgram',
  },
  {
    value: 'winter_program',
    labelKey: 'auditions:generateOffers.offerTypes.winterProgram',
  },
  {
    value: 'internship_program',
    labelKey: 'auditions:generateOffers.offerTypes.internshipProgram',
  },
  {
    value: 'international_scholar',
    labelKey: 'auditions:generateOffers.offerTypes.internationalScholar',
  },
  { value: 'other', labelKey: 'auditions:generateOffers.offerTypes.other' },
];

const OFFER_LENGTHS = [
  { value: 'life', labelKey: 'auditions:generateOffers.lengths.life' },
  { value: 'seasonal', labelKey: 'auditions:generateOffers.lengths.seasonal' },
  {
    value: 'shortTerm',
    labelKey: 'auditions:generateOffers.lengths.shortTerm',
  },
];

const SCHOLARSHIP_TYPES = [
  { value: 'full', labelKey: 'auditions:generateOffers.scholarships.full' },
  {
    value: 'partial',
    labelKey: 'auditions:generateOffers.scholarships.partial',
  },
  {
    value: 'acceptance_only',
    labelKey: 'auditions:generateOffers.scholarships.acceptanceOnly',
  },
];

// ==================== Props ====================

interface GenerateOffersFormProps {
  auditionId: string;
  acceptedCount: number;
  existingOffersCount: number;
  defaultStartDate?: string | null;
  defaultEndDate?: string | null;
}

// ==================== Component ====================

export function GenerateOffersForm({
  auditionId,
  acceptedCount,
  existingOffersCount,
  defaultStartDate,
  defaultEndDate,
}: GenerateOffersFormProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [isGenerating, setIsGenerating] = useState(false);

  // Form state
  const [offerType, setOfferType] = useState<OfferType>('contract');
  const [offerLength, setOfferLength] = useState<string>('');
  const [scholarship, setScholarship] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(
    defaultStartDate ? new Date(defaultStartDate) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    defaultEndDate ? new Date(defaultEndDate) : undefined,
  );
  const [responseDeadline, setResponseDeadline] = useState<Date | undefined>();
  const [comment, setComment] = useState('');
  const [publishImmediately, setPublishImmediately] = useState(false);

  const eligibleCount = acceptedCount - existingOffersCount;

  const handleGenerate = async () => {
    if (eligibleCount <= 0) {
      toast.error(t('auditions:generateOffers.toast.noEligible'));
      return;
    }

    setIsGenerating(true);
    try {
      const result = await generateOffersFromAuditionAction({
        auditionId,
        offerType,
        offerLength: offerLength || null,
        scholarship: scholarship || null,
        startDate: startDate?.toISOString().split('T')[0] ?? null,
        endDate: endDate?.toISOString().split('T')[0] ?? null,
        responseDeadline: responseDeadline?.toISOString().split('T')[0] ?? null,
        organizationComment: comment || null,
        publishImmediately,
      });

      if (result.success) {
        const { created, skipped } = result.data;
        if (created.length > 0) {
          toast.success(
            publishImmediately
              ? t('auditions:generateOffers.toast.generatedAndPublished', {
                  count: created.length,
                })
              : t('auditions:generateOffers.toast.generated', {
                  count: created.length,
                }),
          );
        }
        if (skipped.length > 0) {
          toast.warning(
            t('auditions:generateOffers.toast.skipped', {
              count: skipped.length,
            }),
          );
        }
        router.refresh();
      } else {
        toast.error(
          typeof result.error === 'string'
            ? result.error
            : t('auditions:generateOffers.toast.generateFailed'),
        );
      }
    } catch {
      toast.error(t('auditions:generateOffers.toast.unexpectedError'));
    } finally {
      setIsGenerating(false);
    }
  };

  if (acceptedCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <Trans i18nKey="auditions:generateOffers.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:generateOffers.noAcceptedDescription" />
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <Trans i18nKey="auditions:generateOffers.title" />
        </CardTitle>
        <CardDescription>
          <Trans
            i18nKey="auditions:generateOffers.createDescription"
            values={{ count: eligibleCount }}
          />
          {existingOffersCount > 0 && (
            <span className="text-muted-foreground">
              {' '}
              <Trans
                i18nKey="auditions:generateOffers.alreadyHaveOffers"
                values={{ count: existingOffersCount }}
              />
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Offer Type */}
        <div className="space-y-2">
          <Label htmlFor="offer-type">
            <Trans i18nKey="auditions:generateOffers.offerTypeLabel" />
          </Label>
          <Select
            value={offerType}
            onValueChange={(v) => setOfferType(v as OfferType)}
          >
            <SelectTrigger id="offer-type">
              <SelectValue
                placeholder={t('auditions:generateOffers.offerTypePlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {OFFER_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(type.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Offer Length & Scholarship */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="offer-length">
              <Trans i18nKey="auditions:generateOffers.contractLengthLabel" />
            </Label>
            <Select value={offerLength} onValueChange={setOfferLength}>
              <SelectTrigger id="offer-length">
                <SelectValue
                  placeholder={t('auditions:generateOffers.lengthPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {OFFER_LENGTHS.map((length) => (
                  <SelectItem key={length.value} value={length.value}>
                    {t(length.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scholarship">
              <Trans i18nKey="auditions:generateOffers.scholarshipLabel" />
            </Label>
            <Select value={scholarship} onValueChange={setScholarship}>
              <SelectTrigger id="scholarship">
                <SelectValue
                  placeholder={t(
                    'auditions:generateOffers.scholarshipPlaceholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {SCHOLARSHIP_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {t(type.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Dates */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>
              <Trans i18nKey="auditions:generateOffers.startDateLabel" />
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate
                    ? format(startDate, 'PPP')
                    : t('auditions:generateOffers.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>
              <Trans i18nKey="auditions:generateOffers.endDateLabel" />
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate
                    ? format(endDate, 'PPP')
                    : t('auditions:generateOffers.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>
              <Trans i18nKey="auditions:generateOffers.responseDeadlineLabel" />
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {responseDeadline
                    ? format(responseDeadline, 'PPP')
                    : t('auditions:generateOffers.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={responseDeadline}
                  onSelect={setResponseDeadline}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Comment */}
        <div className="space-y-2">
          <Label htmlFor="comment">
            <Trans i18nKey="auditions:generateOffers.messageLabel" />
          </Label>
          <Textarea
            id="comment"
            placeholder={t('auditions:generateOffers.messagePlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
        </div>

        {/* Publish immediately */}
        <div className="flex items-center space-x-2">
          <Switch
            id="publish-immediately"
            checked={publishImmediately}
            onCheckedChange={setPublishImmediately}
          />
          <Label htmlFor="publish-immediately" className="cursor-pointer">
            <Trans i18nKey="auditions:generateOffers.sendImmediately" />
          </Label>
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || eligibleCount <= 0}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <Trans i18nKey="auditions:generateOffers.generating" />
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              <Trans
                i18nKey="auditions:generateOffers.generateButton"
                values={{ count: eligibleCount }}
              />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
